import { FC, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogDescription,
	DialogHeader,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { t } from 'i18next';
import { useFaqDetailsApi } from '@/features/faq/hooks/useFaqDetailsApi';
import ComposeIcon from '@/components/icons/ComposeIcon';
import Delete_02_Icon from '@/components/icons/Delete_02_Icon';
import EInfoModal from '@/components/everridge/EInfoModal';

type Props = {
	id: string;
	onEdit?: (id: string) => void;
	onClose: () => void;
};

const FaqDetailsMobile: FC<Props> = ({ id, onEdit, onClose }) => {
	const [isOpen, setIsOpen] = useState(true);
	const [showDeleteWarning, setShowDeleteWarning] = useState(false);

	const { faqDetailsData, deleteFaq } = useFaqDetailsApi({ id });

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	return (
		<>
			<Dialog
				open={isOpen}
				onOpenChange={handleClose}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{t('main.faq.components.details.title')}
							<DialogClose
								className={
									'h-11 p-3 ml-auto cursor-pointer border border-popover-100 rounded-full'
								}>
								<Cancel_01_Icon />
							</DialogClose>
						</DialogTitle>
						<DialogDescription />
					</DialogHeader>
					<div className='flex flex-col gap-4'>
						<div className='flex flex-col gap-0.5'>
							<h4 className='text-base font-medium'>
								{t('main.faq.components.details.question')}
							</h4>
							<p className='text-sm text-popover-800'>
								{faqDetailsData?.question ?? ''}
							</p>
						</div>
						<div className='flex flex-col gap-0.5'>
							<h4 className='text-base font-medium'>
								{t('main.faq.components.details.answer')}
							</h4>
							<p className='text-sm text-popover-800'>
								{faqDetailsData?.answer ?? ''}
							</p>
						</div>
					</div>
					<DialogFooter className={'mt-auto'}>
						<Button
							variant='outline'
							className='flex-1 text-error-1000'
							onClick={() => setShowDeleteWarning(true)}>
							<Delete_02_Icon className='stroke-error-1000' />
							{t('elements.buttons.delete')}
						</Button>
						<Button
							variant='outline'
							className='flex-1'
							onClick={() => onEdit?.(id)}>
							<ComposeIcon />
							{t('elements.buttons.edit')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			{showDeleteWarning && (
				<EInfoModal
					label={t('main.faq.components.discard.title')}
					description={t('main.faq.components.discard.description')}
					submitName={t('elements.buttons.delete')}
					submitVariant={'alert'}
					onSubmit={() => deleteFaq(id)}
					closeName={t('elements.buttons.cancel')}
					onClose={() => setShowDeleteWarning(false)}
				/>
			)}
		</>
	);
};

export default FaqDetailsMobile;
